import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import {
  formatAuthError,
  useDevSkipVerification,
  useSignup,
} from "@/features/auth/model/useAuth";
import {
  type EmailVerifiedEvent,
  useEmailVerifiedListener,
} from "@/features/auth/model/verifiedChannel";
import { cn } from "@/shared/lib/cn";
import { formatPhoneInput, nationalPhoneDigits } from "@/shared/lib/phone";
import { Button, FormHint, Input, Label } from "@/shared/ui";

const schema = z
  .object({
    first_name: z.string().trim().min(1, "Required").max(100),
    last_name: z.string().trim().min(1, "Required").max(100),
    email: z.string().email("Enter a valid email"),
    password: z.string().min(8, "At least 8 characters"),
    role: z.enum(["WORKER", "VENUE"], { required_error: "Select an account type" }),
    phone: z
      .string()
      .refine((v) => nationalPhoneDigits(v).length === 10, "Enter a 10-digit phone number"),
    business_name: z.string().trim().max(200).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.role === "VENUE" && !data.business_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["business_name"],
        message: "Required",
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const GOOGLE_SSO_ENABLED = import.meta.env.VITE_GOOGLE_SSO_ENABLED === "true";

// Maps backend error messages to the form field they belong to so we can
// render the error inline instead of as a generic banner.
function fieldFromError(message: string): keyof FormValues | null {
  const m = message.toLowerCase();
  if (m.includes("email")) return "email";
  if (m.includes("password")) return "password";
  if (m.includes("phone")) return "phone";
  return null;
}

export function SignupForm() {
  const signup = useSignup();
  const devSkip = useDevSkipVerification();
  const [showPassword, setShowPassword] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);

  // If the user verifies in the new tab spawned by the email link, flip this
  // tab from "Check your inbox" to a "Verified — sign in" state automatically.
  // Match by email so we don't react to a different signup happening in some
  // other tab.
  const handleVerified = useCallback(
    (event: EmailVerifiedEvent) => {
      const expected = signup.data?.user.email;
      if (expected && event.email === expected) {
        setVerifiedEmail(event.email);
      }
    },
    [signup.data?.user.email],
  );
  useEmailVerifiedListener(handleVerified);
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      role: "WORKER",
      business_name: "",
    },
  });

  const onSubmit = handleSubmit((values) =>
    signup.mutate({
      ...values,
      phone: `+1${nationalPhoneDigits(values.phone)}`,
      business_name: values.role === "VENUE" ? values.business_name?.trim() : undefined,
    }),
  );

  const selectedRole = watch("role");
  const handleGoogleSignup = () => {
    const role = selectedRole ?? "WORKER";
    window.location.href = `/api/v1/auth/google/login?role=${role}&intent=signup`;
  };

  if (signup.isSuccess) {
    const accessToken = signup.data.access_token;
    return (
      <div className="space-y-4">
        {verifiedEmail ? (
          <div className="rounded-lg border border-success bg-success-light p-4 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-foreground" />
              <div className="min-w-0 space-y-2">
                <p className="font-semibold text-success-foreground">Email verified</p>
                <p className="text-success-foreground">
                  <span className="font-medium">{verifiedEmail}</span> is ready to use.
                  You can close the verification tab and sign in here.
                </p>
                <Link
                  to="/login?verified=1"
                  className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-success bg-success-light p-4 text-sm">
            <p className="font-semibold text-success-foreground">Check your inbox</p>
            <p className="mt-1 text-success-foreground/90">
              We sent a verification link to{" "}
              <span className="font-medium">{signup.data.user.email}</span>. Click it to activate
              your account. The link expires in 24 hours.
            </p>
          </div>
        )}

        {import.meta.env.DEV && (
          <div className="rounded-lg border border-warning bg-warning-light p-4 text-sm">
            <p className="font-semibold text-warning-foreground">Demo mode</p>
            <p className="mt-1 text-warning-foreground/90">
              Skip the email verification and jump straight into onboarding.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={devSkip.isPending}
              onClick={() => devSkip.mutate(accessToken)}
            >
              {devSkip.isPending ? "Skipping…" : "Skip verification (demo)"}
            </Button>
            {devSkip.error && (
              <p className="mt-2 text-xs text-destructive-foreground">
                {formatAuthError(devSkip.error)}
              </p>
            )}
          </div>
        )}

        <p className="text-center text-sm text-copy-muted">
          Already verified?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    );
  }

  const roleOptions = [
    { value: "WORKER", label: "I'm a worker", caption: "Find shifts" },
    { value: "VENUE", label: "I'm a venue", caption: "Hire workers" },
  ] as const;

  const submitErrorMessage = signup.error ? formatAuthError(signup.error) : null;
  const submitErrorField = submitErrorMessage ? fieldFromError(submitErrorMessage) : null;
  const emailError = errors.email?.message ?? (submitErrorField === "email" ? submitErrorMessage : null);
  const passwordError =
    errors.password?.message ?? (submitErrorField === "password" ? submitErrorMessage : null);
  const phoneError =
    errors.phone?.message ?? (submitErrorField === "phone" ? submitErrorMessage : null);
  const showBannerError = submitErrorMessage && !submitErrorField;

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {GOOGLE_SSO_ENABLED && (
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleGoogleSignup}
          >
            Continue with Google
          </Button>
          <div className="relative flex items-center" aria-hidden>
            <span className="h-px flex-1 bg-border" />
            <span className="px-3 text-xs uppercase tracking-wide text-copy-muted">or</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-copy">Account type</legend>
        <div className="grid grid-cols-2 gap-2">
          {roleOptions.map(({ value, label, caption }) => (
            <label
              key={value}
              className="group flex cursor-pointer flex-col items-start gap-0.5 rounded-md border border-border-strong bg-surface px-4 py-3 text-sm transition-all hover:border-primary has-[:checked]:border-primary has-[:checked]:bg-primary-light has-[:checked]:shadow-card"
            >
              <input type="radio" value={value} className="sr-only" {...register("role")} />
              <span className="font-semibold text-copy">{label}</span>
              <span className="text-xs text-copy-muted">{caption}</span>
            </label>
          ))}
        </div>
        {errors.role && <FormHint error>{errors.role.message}</FormHint>}
      </fieldset>

      {selectedRole === "VENUE" && (
        <div className="space-y-1.5">
          <Label htmlFor="business_name">Business name</Label>
          <Input
            id="business_name"
            autoComplete="organization"
            placeholder="The Whalesbone"
            invalid={!!errors.business_name}
            {...register("business_name")}
          />
          {errors.business_name ? (
            <FormHint error>{errors.business_name.message}</FormHint>
          ) : (
            <FormHint>The name workers will see on every shift card.</FormHint>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="first_name">
            {selectedRole === "VENUE" ? "Your first name" : "First name"}
          </Label>
          <Input
            id="first_name"
            autoComplete="given-name"
            invalid={!!errors.first_name}
            {...register("first_name")}
          />
          {errors.first_name && <FormHint error>{errors.first_name.message}</FormHint>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="last_name">
            {selectedRole === "VENUE" ? "Your last name" : "Last name"}
          </Label>
          <Input
            id="last_name"
            autoComplete="family-name"
            invalid={!!errors.last_name}
            {...register("last_name")}
          />
          {errors.last_name && <FormHint error>{errors.last_name.message}</FormHint>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          invalid={!!emailError}
          {...register("email")}
        />
        {emailError && <FormHint error>{emailError}</FormHint>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            invalid={!!passwordError}
            className="pr-10"
            {...register("password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className={cn(
              "absolute inset-y-0 right-0 flex items-center px-3 text-copy-muted transition-colors",
              "hover:text-copy focus-visible:outline-none focus-visible:text-copy",
            )}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {passwordError ? (
          <FormHint error>{passwordError}</FormHint>
        ) : (
          <FormHint>At least 8 characters.</FormHint>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="phone">Phone</Label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+1 (613) 555-1234"
              invalid={!!phoneError}
              value={field.value}
              onChange={(e) => field.onChange(formatPhoneInput(e.target.value))}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
        {phoneError && <FormHint error>{phoneError}</FormHint>}
      </div>

      {showBannerError && (
        <div className="rounded-md border border-destructive bg-destructive-light px-3 py-2 text-sm text-destructive-foreground">
          {submitErrorMessage}
        </div>
      )}

      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || signup.isPending}>
        {signup.isPending ? "Creating account…" : "Create account"}
      </Button>

      <p className="text-center text-xs text-copy-muted">
        By creating an account, you agree to our{" "}
        <a
          href="/legal/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-copy-muted underline underline-offset-2 hover:text-copy"
        >
          Terms
        </a>{" "}
        and{" "}
        <a
          href="/legal/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-copy-muted underline underline-offset-2 hover:text-copy"
        >
          Privacy Policy
        </a>
        .
      </p>

      <p className="text-center text-sm text-copy-muted">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  );
}
